import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "../components/TaskTable";
import TaskDrawer from "../components/TaskDrawer";
import { useData } from "../lib/DataContext";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { fmtDate } from "../lib/ui";
import { toast } from "sonner";
import { TrendingUp } from "lucide-react";

const LEVEL_COLOR = { 1: "#2563EB", 2: "#CA8A04", 3: "#EA580C", 4: "#DC2626", 5: "#991B1B" };
const STATUS_COLOR = { Open: "#DC2626", Acknowledged: "#CA8A04", "Intervention Required": "#EA580C", Resolved: "#16A34A", Cancelled: "#94A3B8" };

export default function Escalations() {
  const [status, setStatus] = useState("Open");
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);
  const { userName } = useData();
  const load = () => api.get("/escalations", { params: status === "all" ? {} : { status } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [status]);

  const act = async (id, kind) => {
    await api.post(`/escalations/${id}/${kind}`, { note: "" });
    toast.success(kind === "acknowledge" ? "Acknowledged" : "Resolved");
    load();
  };

  return (
    <div className="p-6">
      <PageHeader title="Escalations" subtitle="Persistent escalation records raised automatically by the engine" />
      <Tabs value={status} onValueChange={setStatus} className="mb-4">
        <TabsList>
          {[["Open","Open"],["Acknowledged","Acknowledged"],["Intervention Required","Intervention"],["Resolved","Resolved"],["all","All"]].map(([k,l]) => (
            <TabsTrigger key={k} value={k} data-testid={`esc-tab-${k.replace(/ /g,"-")}`}>{l}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <div className="space-y-2">
        {items.length === 0 && <div className="text-center py-12 text-slate-400 text-sm">No escalations.</div>}
        {items.map((e) => (
          <div key={e.id} className="bg-white border rounded-lg p-4 flex items-center gap-3" data-testid={`escalation-${e.human_id}`}>
            <div className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: (LEVEL_COLOR[e.level] || "#2563EB") + "20" }}>
              <TrendingUp size={16} style={{ color: LEVEL_COLOR[e.level] || "#2563EB" }} />
            </div>
            <button onClick={() => setOpenId(e.task_id)} className="flex-1 text-left">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[10px] text-slate-400">{e.human_id}</span>
                <span className="text-xs font-semibold px-1.5 py-0.5 rounded text-white" style={{ backgroundColor: LEVEL_COLOR[e.level] || "#2563EB" }}>Level {e.level}</span>
                <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: (STATUS_COLOR[e.status] || "#64748B") + "20", color: STATUS_COLOR[e.status] || "#64748B" }}>{e.status}</span>
                {e.intervention_required && <span className="text-[10px] bg-red-100 text-red-700 rounded-full px-2 py-0.5">Intervention Required</span>}
              </div>
              <div className="font-medium text-slate-800 mt-0.5">{e.task_human_id} — {e.task_title}</div>
              <div className="text-xs text-slate-500">{e.rule_name} · {e.trigger_reason} · {fmtDate(e.triggered_at)}</div>
            </button>
            <div className="flex gap-1.5">
              {e.status === "Open" && <Button size="sm" variant="outline" onClick={() => act(e.id, "acknowledge")} data-testid={`ack-${e.human_id}`}>Acknowledge</Button>}
              {e.status !== "Resolved" && e.status !== "Cancelled" && <Button size="sm" onClick={() => act(e.id, "resolve")} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`resolve-${e.human_id}`}>Resolve</Button>}
            </div>
          </div>
        ))}
      </div>
      {openId && <TaskDrawer taskId={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}
